import { Divider, Drawer } from '@mui/material'
import { create as createNiceModal, useModal } from '@ebay/nice-modal-react'
import { ReactElement } from 'react'
import { I18n, Translate } from 'common'
import styled from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import { Price } from '../../../ui-components/products/Price'
import { Thumbnail } from '../../../ui-components/products/Thumbnail'
import { ShoppingCartProduct } from 'src/store/models/ShoppingCartModels'
import { ProductLocationModel } from 'interfaces'

interface ICartPartDetailsDrawerProps {
  shoppingCartProduct: ShoppingCartProduct
  yourCost?: number
  selectedLocation?: ProductLocationModel
  total?: number
}

export const CartPartDetailsDrawer = createNiceModal(
  (cartPartDetails: ICartPartDetailsDrawerProps): ReactElement => {
    const modal = useModal()
    const thumbnail = cartPartDetails?.shoppingCartProduct.partImages?.length
      ? cartPartDetails.shoppingCartProduct.partImages.find(
          (img) => img.type === 'thumbnail'
        )?.url
      : ''
    return (
      <Drawer
        anchor="bottom"
        open={modal.visible}
        onClose={() => modal.remove()}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(52, 52, 52, 0.2)' },
        }}
      >
        <Container>
          <TitleHeader>
            <StyledText bold>
              <I18n name="costDetails" style={{ fontSize: '22px' }} />
            </StyledText>
            <StyledCloseIcon
              onClick={() => {
                modal.remove()
              }}
            />
          </TitleHeader>
          <Row style={{ marginTop: '25px', marginBottom: '25px' }}>
            <Thumbnail
              urlOrBase64={thumbnail}
              width={150}
              height={150}
              placeholder
            />
            <Column style={{ marginLeft: '10px' }}>
              <StyledText bold underline>
                {cartPartDetails?.shoppingCartProduct.description}
              </StyledText>
              <StyledText>{`${Translate('partNum')} ${
                cartPartDetails?.shoppingCartProduct.partNumber
              }`}</StyledText>
              <StyledText>{`${Translate('brand')} ${
                cartPartDetails?.shoppingCartProduct.brand
              }`}</StyledText>
            </Column>
          </Row>
          <Divider sx={{ opacity: 0.1 }} />
          <StyledText bold style={{ marginTop: '15px', marginBottom: '15px' }}>
            <I18n name="listPriceBreakdown" />
          </StyledText>
          <Row style={{ justifyContent: 'space-between' }}>
            <Column>
              <StyledText>{`${Translate('listPrice')}`}</StyledText>
              <StyledText>{`+ ${Translate('corePrice')}`}</StyledText>
            </Column>
            <Column>
              <StyledText>
                <Price num={cartPartDetails?.selectedLocation.list} />
              </StyledText>
              <StyledText>
                <Price num={cartPartDetails?.selectedLocation.coreList} />
              </StyledText>
            </Column>
          </Row>

          <StyledText bold style={{ marginTop: '15px', marginBottom: '15px' }}>
            <I18n name="costBreakdown" />
          </StyledText>
          <Row style={{ justifyContent: 'space-between' }}>
            <Column>
              <StyledText>{`${Translate('yourCost')}`}</StyledText>
              <StyledText>{`+ ${Translate('coreCost')}`}</StyledText>
            </Column>
            <Column>
              <StyledText>
                <Price num={cartPartDetails?.yourCost} />
              </StyledText>
              <StyledText>
                <Price num={cartPartDetails?.selectedLocation.coreCost} />
              </StyledText>
            </Column>
          </Row>
          <Divider
            sx={{ opacity: 0.1, marginTop: '15px', marginBottom: '15px' }}
          />
          <Row style={{ justifyContent: 'space-between' }}>
            <Column>
              <StyledText bold>{`${Translate('totalCost')}`}</StyledText>
            </Column>
            <Column>
              <StyledText bold>
                <Price num={cartPartDetails?.total} />
              </StyledText>
            </Column>
          </Row>
        </Container>
      </Drawer>
    )
  }
)

const Column = styled.div`
  display: flex;
  flex-direction: column;
`

const Container = styled(Column)`
  height: 600px;
  width: auto;
  margin: 20px 40px;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
`

const TitleHeader = styled(Row)`
  justify-content: space-between;
  font-family: Oswald, serif;
`

interface StyledTextProps {
  bold?: boolean
  underline?: boolean
}

const StyledText = styled.div<StyledTextProps>`
  display: flex;
  color: black;
  font-weight: ${(p) => (p.bold ? 'bold' : 'normal')};
  font-size: 18px;
  text-decoration: ${(p) => (p.underline ? 'underline' : 'none')};
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  margin-top: 5.5px;
`
